import React, { useCallback } from 'react';
import { MedicalProfileContactDetails } from '../../../../../primary-record-types/apiTypes/medicalProfileService';
import { getIntl } from '../../../../../utils/intl';
import ContactDetailsForm, { ContactDetailField } from '../../../../shared/ContactDetailsForm';
import useMedicalProfiles from '../../../../../hooks/api/useMedicalProfiles';
import TempImagePicker, { TempImageValue } from '../../../../v2BaseComponents/ImagePicker/TempImagePicker';
import { ProfilesTabParamList, useGoBack, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import { RouteProp, useFocusEffect } from '@react-navigation/native';
import { User } from '../../../../../primary-record-types/DynamoTypes';
import useUser from '../../../../../hooks/api/useUser';
import { AVATAR_UPLOAD_QUALITY } from '../../../../../utils/constants';
import useRecordEvent from '../../../../../hooks/useRecordEvent';
import Stack, { ContentStack } from '../../../../v2BaseComponents/Stack';
import Text from '../../../../v2BaseComponents/Text';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import InfoIcon from '../../../../v2BaseComponents/InfoIcon';
import ListItem from '../../../../v2BaseComponents/ListItem';
import { useIsMobileLayout } from '../../../../../hooks/useIsMobileLayout';

const intl = getIntl('addMedicalProfile', {
    submitButtonTitle: 'Create Profile',
    skip: 'Skip',
    header1: 'Add Medical Profile (1 of 3)',
    header2: 'Add Medical Profile (2 of 3)',
    header3: 'Add Medical Profile (3 of 3)',
    saveAndAdd: 'Save and Add another',
    save: 'Save',
    success: 'Success',
    successSubText: "'s Medical profile is created!",
    bulletHeader: 'Congratulations. To make Primary Record your own, we recommend starting with the following steps:',
    bullet1: 'Connect and download health information from patient portals.',
    bullet2: 'Organize medications and conditions with bulk edit.',
    bullet3: 'Ask questions and find answers quickly with our Chat Assistant.',
    continue: 'Continue',
    profilePhoto: 'Profile Photo',
    emergencyContact: 'Emergency Contact',
    profileInformationDemographics: 'Demographics',
    profileInformationContactInformation: 'Contact Information',
    addAnotherEmergencyContact: 'Add Another Emergency Contact?',
    yes: 'Yes, Add Another',
    no: 'No, Continue',
    addAnotherEmergencyContactHeader: 'Add Another Emergency Contact?',
    addAnotherEmergencyContactContent:
        'Would you like to add another emergency contact?  You can add as many as you need.',
    profileCreated: 'Profile Created',
});

export type AddMedicalProfileOwnProps = {};

export type AddMedicalProfileProps = AddMedicalProfileOwnProps;

type ContactDetailsInput = Omit<MedicalProfileContactDetails, 'contactDetailsId' | 'sourceEntityId'>;

type StepProps = {
    state: AddMedicalProfileState;
    setState: React.Dispatch<React.SetStateAction<Omit<AddMedicalProfileState, 'isUpdating' | 'onComplete'>>>;
    setStep: (step: Step) => void;
    onCancel: () => void;
};

type Step = 'step1' | 'success';

const getDefaultContactDetails = (user: User | null): ContactDetailsInput => ({
    entityType: 'medical-profile',
    contactType: 'entity-contact',
    firstName: user?.firstName || null,
    lastName: user?.lastName || null,
    preferredFirstName: user?.preferredName || null,
    dob: user?.dob || undefined,
    sex: undefined,
    gender: undefined,
    address1: user?.streetAddress1 || null,
    address2: user?.streetAddress2 || null,
    city: user?.city || null,
    state: user?.state || null,
    zipCode: user?.zipCode || null,
    email: null,
    phoneNumber: user?.phoneNumber || null,
});

const step1ContactDetailsFields: ContactDetailField[] = [
    'firstName',
    'lastName',
    'preferredFirstName',
    'dob',
    'sex',
    'gender',
];

const step1RequiredFields: ContactDetailField[] = ['firstName', 'lastName', 'dob'];

const Step1: React.FC<StepProps> = ({
    state: { tempImage: tempImageState, isUpdating, onComplete, contactDetails },
    setState,
    onCancel,
}) => {
    return (
        <ContactDetailsForm
            key={JSON.stringify(contactDetails)}
            initialValues={contactDetails}
            fields={step1ContactDetailsFields}
            required={step1RequiredFields}
            submitButtonTitle={intl('submitButtonTitle')}
            isDisabled={isUpdating}
            onCancel={onCancel}
            renderFormStart={() => (
                <TempImagePicker
                    sizeVariant="avatar"
                    isDisabled={isUpdating}
                    title={intl('profilePhoto')}
                    value={tempImageState}
                    onChange={(tempFile) => {
                        setState((state) => ({ ...state, tempImage: tempFile }));
                    }}
                    imagePickerOptions={{
                        allowsEditing: true,
                        aspect: [1, 1],
                        quality: AVATAR_UPLOAD_QUALITY,
                    }}
                />
            )}
            onSubmit={(values) => {
                setState((state) => ({ ...state, contactDetails: values as ContactDetailsInput }));
                onComplete(values as ContactDetailsInput);
            }}
        />
    );
};

const ProfileCreatedScreen: React.FC<StepProps> = ({ state: { contactDetails, profileId } }) => {
    const isMobile = useIsMobileLayout();
    const navigation = useProfilesTabNavigation();

    const name =
        contactDetails.firstName || contactDetails.lastName
            ? `${contactDetails.firstName} ${contactDetails.lastName}`
            : '';

    return (
        <ActionButtonLayout
            scrollable
            whiteBackground
            actions={[
                {
                    title: intl('continue'),
                    onPress: () => {
                        if (profileId) {
                            if (isMobile) {
                                navigation.navigate('profile', {
                                    profileId,
                                });
                            } else {
                                navigation.navigate('profile-activity', {
                                    profileId,
                                });
                            }
                        } else {
                            navigation.navigate('root');
                        }
                    },
                    icon: 'ArrowRight',
                    testID: 'profile-created-continue',
                },
            ]}
        >
            <MainContentContainer>
                <ContentStack>
                    <InfoIcon
                        alertIconProps={{
                            variant: 'success',
                            name: 'Check',
                            size: 'lg',
                        }}
                        title={intl('success')}
                        subText={name + intl('successSubText')}
                    />
                    <Stack horizontal fullWidth justify="center">
                        <Stack
                            vertical
                            fullWidth
                            space={1}
                            style={{
                                maxWidth: 600,
                            }}
                        >
                            <Text variant="body-1">{intl('bulletHeader')}</Text>
                            {([{ text: 'bullet1' }, { text: 'bullet2' }, { text: 'bullet3' }] as const).map(
                                (bullet, index) => (
                                    <ListItem unordered index={index} key={index} variant="body-1">
                                        <Text variant="body-1" style={{ flex: 1 }}>
                                            {intl(bullet.text)}
                                        </Text>
                                    </ListItem>
                                ),
                            )}
                        </Stack>
                    </Stack>
                </ContentStack>
            </MainContentContainer>
        </ActionButtonLayout>
    );
};

type AddMedicalProfileState = {
    tempImage: TempImageValue | null;
    profileId: string | null;
    contactDetails: ContactDetailsInput;
    onComplete: (contactDetails: ContactDetailsInput) => void;
    isUpdating: boolean;
};

type AddMedicalProfileStateContext = {
    state: AddMedicalProfileState;
    setState: React.Dispatch<React.SetStateAction<Omit<AddMedicalProfileState, 'isUpdating' | 'onComplete'>>>;
};

const AddMedicalProfileStateContext = React.createContext<AddMedicalProfileStateContext>({
    state: {
        contactDetails: getDefaultContactDetails(null),
        tempImage: null,
        profileId: null,
        onComplete: () => {},
        isUpdating: false,
    },
    setState: () => {
        return;
    },
});

export type AddMedicalProfileRouteProps = {
    route: RouteProp<ProfilesTabParamList, 'add'>;
};

const AddProfileScreen: React.FC<AddMedicalProfileProps & AddMedicalProfileRouteProps> = (props) => {
    const goBack = useGoBack();
    const uniqueFormKey = React.useRef(0);
    useFocusEffect(useCallback(() => () => uniqueFormKey.current++, []));
    const { user } = useUser({});
    const [state, setState] = React.useState<Omit<AddMedicalProfileState, 'isUpdating' | 'onComplete'>>({
        contactDetails: getDefaultContactDetails(
            props.route.params?.useUser === 'true' ? (user === 'loading' ? null : user) : null,
        ),
        tempImage: null,
        profileId: null,
    });
    const [step, setStep] = React.useState<Step>('step1');
    const recordEvent = useRecordEvent();

    const { createMedicalProfile, isCreatingProfile } = useMedicalProfiles({
        noCreateSuccessNotification: true,
        onCreateMedicalProfileSuccess: (data) => {
            setState((state) => ({ ...state, profileId: data.medicalProfileId }));
            setStep('success');
        },
    });
    const onComplete = React.useCallback(
        (contactDetails: ContactDetailsInput) => {
            setState((state) => ({ ...state, contactDetails }));
            recordEvent('createMedicalProfile');
            createMedicalProfile({
                contactDetails,
                emergencyContactDetails: [],
                avatar: state.tempImage?.tempFileId
                    ? {
                          tempAvatarFileId: state.tempImage.tempFileId,
                          type: 'updated',
                      }
                    : {
                          type: 'not-modified',
                      },
                isUserMedicalProfile: props.route.params?.useUser === 'true',
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [createMedicalProfile, state.contactDetails, props.route.params?.useUser, recordEvent],
    );

    const StepComponent = {
        step1: Step1,
        success: ProfileCreatedScreen,
    }[step];

    return (
        <AnimateLoadedContent isLoading={isCreatingProfile} key={uniqueFormKey.current}>
            {() => (
                <StepComponent
                    state={{ ...state, isUpdating: isCreatingProfile, onComplete }}
                    setState={setState}
                    setStep={setStep}
                    onCancel={goBack}
                />
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(AddProfileScreen);
